import React, { FC, useEffect } from 'react';

import { SxProps, Box } from '@mui/material';

import {
  AdminLegendVariant,
  IAvailableWorkshiftData,
  adminActions,
  logos,
  userPreferredTimeFormat,
  resetStatus,
  adminWatchlistedWorkshifts,
} from '@pharmaplan/common';
import {
  setBack,
  setDrawerContentPadding,
  setFooterShow,
  setTitle,
} from '../../../actions/drawerActions';

import useAvailableWorkshiftActions from '../../../hooks/AvailableWorkshift/useAvailableWorkshiftActions';
import AdminLongTermUserRates from '../../common/AdminHourlyRateFooter/AdminLongTermUserRates';
import { ReactComponent as Location } from '../../../assets/svg/location.svg';
import getLongTermContractLabels from '../../common/LongTermContractLabels';
import AdminHourlyRateFooter from '../../common/AdminHourlyRateFooter';
import DrawerOutOfDateTitle from '../../common/DrawerOutOfDateTitle';
import useMultiShiftFilter from '../../../hooks/useMultiShiftFilter';
import CustomAvatarSection from '../../common/CustomAvatarSection';
import useAdminEvent from '../../../hooks/Admin/useAdminEvent';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import ThemeConstants from '../../../theme/ThemeConstants';
import strings from '../../../localization';

import { workshiftListBuilder } from './helpers';
import styles from './styles';

export const LocationText: FC<{
  label: string | React.ReactNode;
  containerStyle?: SxProps;
}> = ({ label, containerStyle }) =>
  (
    <Box sx={[styles.locationContainer, containerStyle] as SxProps}>
      <Location
        fill={ThemeConstants.grey[600]}
        height={13}
        width={10}
        style={styles.locationIcon}
      />
      {label}
    </Box>
  );

interface IAvailableWorkshift {
  viewOnly: boolean;
  date?: string;
  pharmacistId?: string;
}

const AvailableWorkshifts: FC<IAvailableWorkshift> = ({
  pharmacistId = '',
  date = '',
  viewOnly = false,
}) => {
  const dispatch = useAppDispatch();

  const { adminEvents: availableWorkshifts } = useMultiShiftFilter();
  const allLogos = useAppSelector(logos);
  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const watchListedWorkshifts = useAppSelector(adminWatchlistedWorkshifts);

  const eventHandler = viewOnly ? watchListedWorkshifts : availableWorkshifts;
  const successAction = viewOnly
    ? adminActions.watchlists
    : adminActions.getAdminEvent;

  const { actions, goToViewDetails, watchlistViewDetails } = useAvailableWorkshiftActions();
  const { getAdminEventByDate } = useAdminEvent();

  useEffect(() => {
    const title = !viewOnly ? (
      <DrawerOutOfDateTitle
        dateLabel={strings.availableWorkshiftFormatted}
        date={date}
      />
    ) : (
      strings.watchlistedPharmacyWorkshifts
    );

    if (!viewOnly) {
      getAdminEventByDate({
        type: AdminLegendVariant.posted,
        selectedDate: date.split('T')[0],
      });
    }

    dispatch(setBack(null));
    dispatch(setTitle({ title }));
    dispatch(setDrawerContentPadding('0px'));
    dispatch(setFooterShow(false));

    return () => {
      dispatch(resetStatus([successAction]));
      dispatch(setDrawerContentPadding('16px'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const events = workshiftListBuilder(
    eventHandler as IAvailableWorkshiftData[],
    allLogos,
    timeFormat,
    viewOnly,
  );

  return (
    <>
      {events.map((item) => {
        const {
          config,
          title,
          avatar,
          workshiftId,
          pharmacistHourlyRate,
          pharmacyHourlyRate,
          pharmacyId,
          startDate,
          endDate,
          isRequested,
          replacementType,
          isAutoSyncRecord,
          weekDayHourlyRate,
          weekendHourlyRate,
          contractEndDate,
          contractStartDate,
          totalShiftCount,
          bookedShiftCount,
          pharmacyWeekDayHourlyRate,
          pharmacyWeekendHourlyRate,
        } = item ?? {};

        const menuActions = viewOnly
          ? null
          : actions({
            id: workshiftId,
            startDate,
            endDate,
            pharmacyId,
            isAutoSyncRecord,
            isLongTerm: !!contractStartDate,
            postedCount: totalShiftCount ?? 0 - bookedShiftCount ?? 0,
          });

        const handleClick = () => {
          if (viewOnly) {
            watchlistViewDetails({
              workshiftId,
              pharmacistId,
              pharmacyId,
              isRequested,
            });
          } else {
            goToViewDetails({
              workshiftId,
              date,
              pharmacyId,
              isAutoSyncRecord,
            });
          }
        };

        const { ShiftLabel, RangeLabel } = getLongTermContractLabels({
          successAction,
          contractEndDate,
          contractStartDate,
          isLongTerm: !!contractStartDate,
        });

        const { customPharmacistChip, customPharmacyChip } = AdminLongTermUserRates({
          pharmacyWeekDayHourlyRate,
          pharmacyWeekendHourlyRate,
          weekDayHourlyRate,
          weekendHourlyRate,
          successAction,
          contractStartDate,
        });

        const rangeConfig = [
          { key: '5', value: <RangeLabel sx={styles.zeroMargin} /> },
        ];

        return (
          <Box key={workshiftId} sx={styles.buttonContainer}>
            <CustomAvatarSection
              TopSection={<ShiftLabel sx={styles.shiftLabel} />}
              customMenuContainerStyle={styles.menu}
              successAction={successAction}
              config={[...rangeConfig, ...config]}
              title={title}
              avatar={avatar}
              showFullImage={false}
              onClick={handleClick}
              imageSuccessAction={successAction}
              menuActions={isAutoSyncRecord ? null : menuActions}
              isAutoSyncRecord={isAutoSyncRecord}
            />
            <Box sx={styles.footerContainer}>
              <AdminHourlyRateFooter
                pharmacyHourlyRate={pharmacyHourlyRate as number}
                pharmacistHourlyRate={pharmacistHourlyRate as number}
                successAction={successAction}
                replacementType={replacementType}
                customPharmacistChip={customPharmacistChip}
                customPharmacyChip={customPharmacyChip}
              />
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default AvailableWorkshifts;
